import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDiscoverConfig, useItemPrice } from "../config";
import { Link, useParams } from "@tanstack/react-router";
import type { DiscoverItem } from "../types";
import { isTrip } from "../lib/is-trip";
import type { DiscoverCity, VisibilityFilter } from "../types";
import { DiscoverMobileMapBackdrop } from "./discover-mobile-map-backdrop";
import { DraggableSheet, type SheetHandle } from "./discover-mobile-sheet";
import {
  useDiscoverActions,
  useDiscoverContext,
} from "../state/discover-context";
import { inViewItems } from "../hooks/use-in-view-items";
import { matchesSubtags } from "@majourneys/shared";
import { BUCKETS, type BucketId } from "../lib/buckets";
import {
  CategoryChipRow,
  CityPillRow,
  SubFilterRow,
  SubtagPillRow,
  deriveSubFilters,
} from "./discover-sheet-facets";
import { thumbSrcSet, thumbUrl } from "./map/thumb-url";

import { Icon } from "@majourneys/icons";
import { SelectMenu, type MenuOption } from "@majourneys/ui";
import { BEVEL_CHIP_ON, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

export { countsState, type CountsState } from "../lib/counts-state";
import type { CountsState } from "../lib/counts-state";

interface Props {
  readonly items: readonly DiscoverItem[];
  readonly cities: readonly DiscoverCity[];
  readonly onSelectItem?: (id: string) => void;
  readonly counts?: CountsState;
}

/**
 * The mobile Discover sheet, ported from the phone (apps/mobile
 * explore-surface.tsx): search field, filter rows, then a flat two-column grid
 * of venues. A traveller sees real places on the first screen.
 *
 * It used to be a drill-down — a stack of per-city sections, each a rail of
 * category cards — so reaching a single venue cost a city tap and a category
 * tap. The phone has never asked for either, and the owner asked for the
 * phone's sheet. City and category are now filters over one flat list rather
 * than two levels you descend through.
 */

function matchesQuery(item: DiscoverItem, query: string): boolean {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.title.toLowerCase().includes(q) ||
    item.location.toLowerCase().includes(q) ||
    item.subtitle.toLowerCase().includes(q)
  );
}


/** Every browse filter except the city — see `cityCounts`. */
interface BrowseFilters {
  readonly activeType: BucketId | null;
  readonly searchQuery: string;
  readonly subtags: readonly string[];
  readonly visibility: VisibilityFilter;
}

function matchesBrowse(item: DiscoverItem, f: BrowseFilters): boolean {
  if (f.visibility === "bookable" && item.mode !== "bookable") return false;
  if (f.visibility === "explore" && item.mode !== "explore") return false;
  if (f.activeType) {
    const bucket = BUCKETS.find((b) => b.id === f.activeType);
    if (bucket && !bucket.match(item)) return false;
  }
  if (!matchesQuery(item, f.searchQuery)) return false;
  if (!matchesSubtags(item, f.subtags)) return false;
  return true;
}

/**
 * How much of the sheet's scrolling content stays on screen once it is
 * collapsed onto the map: the search field (a 24px input in a pill with 10px
 * of padding) and the 4px above and below it.
 */
const SEARCH_STRIP_PX = 52;

export function ConceptACompactMap({ items, cities, onSelectItem, counts = "ready" }: Props) {
  const { t } = useTranslation();
  const { bucketArt } = useDiscoverConfig();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCity, setActiveCity] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<BucketId | null>(null);
  // The kind picked inside the active category ("Villa" under Stays). A plain
  // label rather than an id because that is what the catalogue gives us — the
  // same string the card's own badge shows.
  const [subLabel, setSubLabel] = useState<string | null>(null);
  // Like-state is the single source of truth in the discover context
  // (persisted to localStorage and shared with the cards, detail slide-over,
  // and the Liked tab) — never a component-local copy, or the explore grid
  // and the Liked tab would drift apart.
  const { state } = useDiscoverContext();
  const { toggleLiked, setViewportBounds, setSubtags, setVisibility } =
    useDiscoverActions();
  const liked = state.likedIds;
  const filterBarRef = useRef<HTMLDivElement | null>(null);
  const sheetHandle = useRef<SheetHandle | null>(null);

  // A multi-stop trip is a route, not a place: dropping one marker on its
  // departure city tells the traveller something untrue. Dropped here, at the
  // source, rather than inside the filter below — so the map, the tile counts
  // and the list all describe the same catalogue. Trips stay bookable and
  // fully browsable elsewhere; they just don't belong on a map of places.
  const places = useMemo(() => items.filter((it) => !isTrip(it)), [items]);

  const browseFilters = useMemo<BrowseFilters>(
    () => ({
      activeType,
      searchQuery,
      subtags: state.selectedSubtags,
      visibility: state.visibility,
    }),
    [activeType, searchQuery, state.selectedSubtags, state.visibility],
  );

  // Everything except the kind pills. Split out because those pills need
  // counts, and a facet must never count itself out: filtering by "Villa" and
  // then counting Villa against the result would leave every other kind at
  // zero.
  const baseItems = useMemo(
    () =>
      places.filter(
        (it) =>
          (!activeCity || it.citySlug === activeCity) &&
          matchesBrowse(it, browseFilters),
      ),
    [places, activeCity, browseFilters],
  );

  // When the user zooms the map in, narrow the sheet to the items in view
  // (flat, nearest-first) instead of the whole browse.
  const inView = useMemo(
    () => inViewItems(baseItems, state.viewportBounds),
    [baseItems, state.viewportBounds],
  );

  // The kinds on offer are counted off the rows actually on screen, so a
  // category only ever offers what it holds here — see deriveSubFilters.
  const subFilters = useMemo(
    () => deriveSubFilters(inView.items, activeType),
    [inView.items, activeType],
  );

  // A kind that has left the view is not a filter any more, it is a dead end:
  // moving from Marrakech to Essaouira while "Riad" is picked would otherwise
  // strand the traveller on an empty grid with no pill to un-press. Dropping
  // it here rather than resetting on every city change keeps the choice alive
  // as long as it still leads somewhere.
  const activeSubLabel =
    subLabel && subFilters.some((option) => option.key === subLabel)
      ? subLabel
      : null;

  const ofKind = useCallback(
    (list: readonly DiscoverItem[]) =>
      activeSubLabel
        ? list.filter((it) => it.pinLabel?.trim() === activeSubLabel)
        : list,
    [activeSubLabel],
  );

  /** What the grid lists: the browse, narrowed by the map and by the kind. */
  const listItems = useMemo(() => ofKind(inView.items), [ofKind, inView.items]);

  /** What the map pins: the same catalogue, minus the map's own narrowing. */
  const mapItems = useMemo(() => ofKind(baseItems), [ofKind, baseItems]);

  // What each city would give you if you picked it. Counted with every live
  // filter EXCEPT the city itself: a facet must never count itself out, or the
  // moment you choose Marrakech every other city in the menu reads zero.
  const cityCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const it of places) {
      if (!it.citySlug) continue;
      if (!matchesBrowse(it, browseFilters)) continue;
      if (activeSubLabel && it.pinLabel?.trim() !== activeSubLabel) continue;
      counts.set(it.citySlug, (counts.get(it.citySlug) ?? 0) + 1);
    }
    return counts;
  }, [places, browseFilters, activeSubLabel]);

  // Never offer a filter that leads nowhere. The map already hides zero-count
  // city pins; matching the menu to it stops a dead-end pick like Agafay
  // selecting the traveller into an empty grid. The city already chosen stays
  // listed whatever its count — that one is not an offer, it is where you are.
  const citiesWithSpots = useMemo(
    () =>
      cities.filter(
        (c) => (cityCounts.get(c.slug) ?? 0) > 0 || c.slug === activeCity,
      ),
    [cities, cityCounts, activeCity],
  );

  // One line saying how big the grid below it is. With the drill-down gone
  // there are no city sections left to count, so a chosen city states its own
  // total and the whole-country browse says how many cities are behind it.
  const totalsLabel = inView.active
    ? t("discover.picks_visible_short", { count: listItems.length })
    : activeCity
      ? t("discover.group_spots", { count: listItems.length })
      : t("discover.spots_summary", {
          count: citiesWithSpots.length,
          groups: citiesWithSpots.length,
          spots: listItems.length,
        });

  const activeCityObject = activeCity
    ? cities.find((c) => c.slug === activeCity) ?? null
    : null;
  const sheetTitle = activeCityObject
    ? activeCityObject.name
    : t("discover.region.morocco");

  const resetAll = () => {
    setActiveCity(null);
    setActiveType(null);
    setSubLabel(null);
    setSearchQuery("");
    // Also drop any map-viewport narrowing so the × returns to the full browse.
    setViewportBounds(null);
    setSubtags([]);
    // The bookable/all control now sits in the same header as this ×, so a
    // traveller reading "clear" expects it cleared too.
    setVisibility("all");
  };

  // Selecting/deselecting the restaurants bucket, or switching to a different
  // bucket entirely, drops any sub-tag selection — otherwise a stale
  // "rooftop" filter would silently persist into e.g. the Stays bucket, or
  // survive toggling Restaurants off. The kind pills go with it: they belong
  // to the category that offered them.
  const handleSelectType = (id: BucketId) => {
    const next = activeType === id ? null : id;
    setActiveType(next);
    setSubLabel(null);
    if (next !== "restaurants") setSubtags([]);
  };

  // A new question deserves the top of the answer. Honours reduced motion,
  // which a bare `behavior: "smooth"` would ignore.
  const focusFilters = () => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    filterBarRef.current?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
  };

  const pickCity = (slug: string | null) => {
    setActiveCity(slug);
    focusFilters();
  };

  return (
    <>
      <DiscoverMobileMapBackdrop
        items={mapItems}
        activeCitySlug={activeCity}
        onSelectItem={(id) => onSelectItem?.(id)}
        onBoundsChange={setViewportBounds}
      />
      <MapSurfaceFurniture
        picks={mapItems.length}
        counts={counts}
        onOpenMap={() => sheetHandle.current?.snapTo("collapsed")}
      />
      <DraggableSheet
        handleRef={sheetHandle}
        collapsedExtraPx={SEARCH_STRIP_PX}
        header={
          <RoamySheetHeader
            title={sheetTitle}
            cities={citiesWithSpots}
            cityCounts={cityCounts}
            activeCity={activeCity}
            onPickCity={setActiveCity}
            visibility={state.visibility}
            onSetVisibility={setVisibility}
            onReset={resetAll}
          />
        }
      >
        <div ref={filterBarRef}>
          <RoamySearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
        </div>

        <CategoryChipRow
          buckets={BUCKETS}
          activeId={activeType}
          onSelect={handleSelectType}
          art={bucketArt}
        />

        {activeType === "restaurants" ? (
          <SubtagPillRow
            selected={state.selectedSubtags}
            onToggle={(id) =>
              setSubtags(
                state.selectedSubtags.includes(id)
                  ? state.selectedSubtags.filter((s) => s !== id)
                  : [...state.selectedSubtags, id],
              )
            }
          />
        ) : null}

        <SubFilterRow
          options={subFilters}
          active={activeSubLabel}
          onSelect={setSubLabel}
        />

        <RoamyStatsRow label={totalsLabel} counts={counts} />

        <div className="mt-2 flex flex-col gap-4 pb-8">
          {/*
            The city pills are the grid's header, exactly as they are on the
            phone: they scroll away with the list rather than sitting in fixed
            chrome. Rendered even when the grid is empty — picking a different
            city is the most likely way out of an empty result, so taking the
            pills away at that moment removes the fix along with the problem.
          */}
          {citiesWithSpots.length > 0 && (
            <CityPillRow
              cities={citiesWithSpots}
              counts={cityCounts}
              activeCity={activeCity}
              onPickCity={pickCity}
            />
          )}

          {counts === "loading" ? null : counts === "failed" ? (
            <EmptyState failed />
          ) : listItems.length === 0 ? (
            <EmptyState inView={inView.active} onClear={resetAll} />
          ) : (
            <DrilldownList
              items={listItems}
              liked={liked}
              onToggleLike={toggleLiked}
              onSelectItem={onSelectItem}
              onClear={resetAll}
            />
          )}
        </div>
      </DraggableSheet>
    </>
  );
}

/**
 * The map's own furniture: a count pill in the top corner, and the control
 * that hands the map back once the sheet has covered it.
 *
 * The pill is decorative — the sheet's stats row is the accessible copy of the
 * same number — so it is hidden from the accessibility tree rather than read
 * out twice.
 *
 * "Open map" only exists while the sheet is expanded to full, which is exactly
 * when the phone bar has slid away, so the bottom of the screen never holds
 * both (that show/hide is CSS, off the `data-mj-discover-sheet` attribute the
 * sheet publishes on <html>). A button that says "Open map" has to actually
 * open it: it asks the sheet for its collapsed snap by name, because the
 * header's own tap only steps down to peek, which still covers most of the map
 * the button just promised.
 */
function MapSurfaceFurniture({
  picks,
  counts,
  onOpenMap,
}: {
  readonly picks: number;
  readonly counts: CountsState;
  readonly onOpenMap: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      {/* While loading a bone stands in for the number; if the count failed
          the pill goes rather than claim "0 picks". */}
      {counts === "failed" ? null : (
        <div className="mj-discover-map-pill" aria-hidden="true" data-counts={counts}>
          <Icon name="ri-ticket-2-line" aria-hidden="true" />
          {counts === "loading" ? (
            <>
              <span className="mj-discover-bone" style={{ width: 30 }} />
              {t("discover.picks_label")}
            </>
          ) : (
            t("discover.picks_count", { count: picks })
          )}
        </div>
      )}
      <button type="button" className="mj-discover-map-open" onClick={onOpenMap}>
        <Icon name="ri-map-2-line" aria-hidden="true" />
        {t("discover.open_map")}
      </button>
    </>
  );
}

// Full-width search bar — always visible, fills the row the old All/City chips
// occupied. City switching moved to the sheet title (RoamySheetHeader).
function RoamySearchBar({
  searchQuery,
  onSearchChange,
}: {
  readonly searchQuery: string;
  readonly onSearchChange: (q: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-1 pt-1 sm:px-5">
      {/*
        A <label>, not a <div>: the input is only 21px tall inside a 41px pill,
        so as a plain div the pill's padding was dead space and the real tap
        target fell under the 24x24 minimum. Wrapping in a label makes the
        whole pill focus the input.
      */}
      <label className="flex items-center gap-2 rounded-full bg-[#040033]/[0.06] px-4 py-2.5">
        <Icon name="ri-search-line" className="text-[17px] text-[#040033]/55" aria-hidden="true" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("discover.search_placeholder")}
          aria-label={t("discover.search_placeholder")}
          className="min-h-[24px] min-w-0 flex-1 bg-transparent font-sans text-[14px] text-[#040033] outline-none placeholder:text-[#040033]/60"
        />
        {searchQuery && (
          <button
            type="button"
            aria-label={t("discover.filter.close_search")}
            onClick={() => onSearchChange("")}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#040033]/[0.1] text-[#040033]"
          >
            <Icon name="ri-close-line" className="text-[15px]" aria-hidden="true" />
          </button>
        )}
      </label>
    </div>
  );
}

// Sheet title that doubles as the city switcher: tapping "Morocco" (or the
// active city) opens the city list. `data-no-drag` keeps taps from dragging /
// toggling the sheet. The × resets everything (city + category + search +
// what's on show). The second row is the bookable/all filter — the phone had
// no way to reach it at all, so a filter that exists in the state and on the
// desktop simply did nothing here.
function RoamySheetHeader({
  title,
  cities,
  cityCounts,
  activeCity,
  onPickCity,
  visibility,
  onSetVisibility,
  onReset,
}: {
  readonly title: string;
  readonly cities: readonly DiscoverCity[];
  readonly cityCounts: ReadonlyMap<string, number>;
  readonly activeCity: string | null;
  readonly onPickCity: (slug: string | null) => void;
  readonly visibility: VisibilityFilter;
  readonly onSetVisibility: (value: VisibilityFilter) => void;
  readonly onReset: () => void;
}) {
  const { t } = useTranslation();

  // Every city the sheet is still offering, each with what it would give you.
  // The list is already pruned upstream: a city that leads nowhere is never
  // offered, and the one you are standing in stays listed whatever its count.
  const options = useMemo<MenuOption[]>(
    () => [
      { value: "", label: t("discover.filter.all_cities") },
      ...cities.map((c) => ({
        value: c.slug,
        label: c.name,
        count: cityCounts.get(c.slug) ?? 0,
      })),
    ],
    [cities, cityCounts, t],
  );

  return (
    <header className="px-5 pb-2 pt-1">
      <div className="flex items-center justify-between gap-3">
        {/*
          The sheet's own title is the city switcher. Twelve rows hanging off a
          22px heading on a 375px phone is exactly what the shared control's
          phone body replaces: the list now comes up from the bottom of the
          screen with a handle, a name and a scrim, and it is portalled to
          <body> because this sheet carries a transform, which would otherwise
          pin anything fixed inside it to the sheet instead of the screen.

          `body="sheet"` rather than a media query: this whole surface only
          exists under 860px, so the sheet is always the right body for it.
        */}
        <SelectMenu
          name="discover-sheet-city"
          body="sheet"
          // A flex item will not shrink below its content unless it is told it
          // may, and a long city name has to wrap rather than push the ×
          // off the row. Inline rather than `min-w-0`, because Tailwind does
          // not scan this package.
          style={{ minWidth: 0 }}
          options={options}
          value={activeCity ?? ""}
          onChange={(next) => onPickCity(next === "" ? null : next)}
          labels={{
            title: t("discover.sheet.city_title", { defaultValue: "Where to" }),
            listLabel: t("discover.cities"),
            searchPlaceholder: t("discover.search_cities"),
            clearSearch: t("common.menu_clear_search", { defaultValue: "Clear the search" }),
            closeList: t("common.menu_close_list", { defaultValue: "Close the list" }),
            emptyTitle: t("discover.empty_no_picks"),
            emptyHint: t("discover.empty_try_other"),
            noMatchTitle: (query) => t("discover.no_cities_match", { query }),
            noMatchHint: t("common.menu_no_match_hint", {
              defaultValue: "Try a shorter word, or clear the search.",
            }),
          }}
          renderTrigger={({ ref, buttonProps }) => (
            <button
              {...buttonProps}
              ref={ref}
              data-no-drag
              className="flex min-w-0 items-center gap-1.5"
            >
              {/* The sheet's title wraps rather than cuts, in every language. */}
              <h2
                className="m-0 font-sans text-[22px] font-extrabold leading-tight text-[#040033]"
                style={{ minWidth: 0, textAlign: "start", textWrap: "balance", overflowWrap: "anywhere" }}
              >
                {title}
              </h2>
              <Icon
                name="ri-arrow-down-s-line"
                className="shrink-0 text-[20px] text-[#040033]/70"
                aria-hidden="true"
              />
            </button>
          )}
        />
        <button
          type="button"
          data-no-drag
          aria-label={t("discover.sheet.close_filters")}
          onClick={onReset}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#040033]/[0.06] text-[#040033] transition hover:bg-[#040033]/[0.12]"
        >
          <Icon name="ri-close-line" className="text-[18px]" aria-hidden="true" />
        </button>
      </div>
      <VisibilityChips value={visibility} onChange={onSetVisibility} />
    </header>
  );
}

const VISIBILITY_OPTIONS = [
  { id: "all", labelKey: "discover.visibility.all" },
  { id: "bookable", labelKey: "discover.visibility.bookable" },
  { id: "explore", labelKey: "discover.visibility.explore" },
] as const satisfies readonly {
  readonly id: VisibilityFilter;
  readonly labelKey: string;
}[];

// The app puts this on the map's top bar; on web that bar belongs to the map
// itself, so it lives here instead — one row under the city name, above the
// search field, where it is on screen even at the sheet's collapsed snap.
// Colours are inline rather than utility classes: this package is outside the
// Tailwind build's scan, so a colour written only here would never be emitted.
// It wraps rather than scrolls, because a row that runs off the side of a
// phone is the one thing this surface must never do.
function VisibilityChips({
  value,
  onChange,
}: {
  readonly value: VisibilityFilter;
  readonly onChange: (value: VisibilityFilter) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="group"
      data-no-drag
      aria-label={t("discover.filter.visibility", { defaultValue: "Show" })}
      className="mt-1.5 flex flex-wrap items-center gap-2"
    >
      {VISIBILITY_OPTIONS.map((option) => {
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            data-no-drag
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            style={{
              background: active ? BEVEL_NAVY_FACE : BEVEL_WHITE_FACE,
              color: active ? "#ffffff" : "#110E39",
              boxShadow: active ? BEVEL_CHIP_ON : `${BEVEL_INSET_WHITE}, inset 0 0 0 1px rgba(4, 0, 51, 0.12)`,
            }}
            className="min-h-[24px] shrink-0 rounded-full px-3 py-1.5 font-sans text-[13px] font-semibold"
          >
            {t(option.labelKey)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The totals line. While the catalogue loads two bones hold the numbers'
 * place, with the words for a screen reader; if it failed, the line hides.
 * It never says "0 cities · 0 spots total" about a page that is still loading.
 */
function RoamyStatsRow({
  label,
  counts = "ready",
}: {
  readonly label: string;
  readonly counts?: CountsState;
}) {
  const { t } = useTranslation();
  if (counts === "failed") return null;
  return (
    <div className="mt-1 px-3 py-2 sm:px-5" data-counts={counts}>
      {counts === "loading" ? (
        <p
          role="status"
          className="m-0 flex items-center gap-1.5 font-sans text-[12.5px] font-medium text-[#5a5c75]"
          style={{ minHeight: 19 }}
        >
          <span className="mj-discover-bone" style={{ width: 58 }} aria-hidden="true" />
          <span aria-hidden="true">·</span>
          <span className="mj-discover-bone" style={{ width: 84 }} aria-hidden="true" />
          <span className="sr-only">{t("discover.counts_loading")}</span>
        </p>
      ) : (
        <p className="m-0 font-sans text-[12.5px] font-medium text-[#5a5c75]">{label}</p>
      )}
    </div>
  );
}

/**
 * How many cards the grid builds before the traveller has scrolled anywhere.
 * Eight, the same first page the phone's list takes.
 */
const FIRST_PAGE = 8;
/** How many more each time the bottom of the grid comes into range. */
const PAGE_STEP = 12;

export function DrilldownList({
  items,
  liked,
  onToggleLike,
  onSelectItem,
  onClear,
}: {
  readonly items: readonly DiscoverItem[];
  readonly liked: ReadonlySet<string>;
  readonly onToggleLike: (id: string) => void;
  readonly onSelectItem?: (id: string) => void;
  /** Omitted by the Liked tab, whose emptiness no filter reset can fix. */
  readonly onClear?: () => void;
}) {
  const { t } = useTranslation();
  // The catalogue runs to ~665 rows and picking Restaurants alone lands 300+
  // of them here. Building a card each on mount is what stalls the sheet, so
  // the grid holds a page and grows as the traveller reaches the bottom of it.
  const [shown, setShown] = useState(FIRST_PAGE);
  const sentinelRef = useRef<HTMLButtonElement | null>(null);
  const hasMore = shown < items.length;

  // A new filter is a new list; it must start at its first page, or a
  // traveller who scrolled deep into Restaurants would land in the middle of
  // Stays.
  useEffect(() => setShown(FIRST_PAGE), [items]);

  // The "show more" button doubles as the scroll sentinel: watched, it grows
  // the grid before the traveller ever reads it; pressed, it does the same
  // thing for a keyboard or for a browser without an observer. One control,
  // both ways in. Re-observed on every page because an observer only reports
  // a *change* of intersection, and a sentinel still on screen after a grow
  // would otherwise never fire again.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown((n) => Math.min(n + PAGE_STEP, items.length));
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, items.length]);

  if (items.length === 0) return <EmptyState onClear={onClear} />;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 px-3 sm:px-5">
        {items.slice(0, shown).map((it) => (
          <MJCard
            key={it.id}
            item={it}
            isLiked={liked.has(it.id)}
            onToggleLike={() => onToggleLike(it.id)}
            onSelect={onSelectItem ? () => onSelectItem(it.id) : undefined}
          />
        ))}
      </div>
      {hasMore && (
        <div className="mt-3 px-3 sm:px-5">
          <button
            ref={sentinelRef}
            type="button"
            onClick={() => setShown((n) => Math.min(n + PAGE_STEP, items.length))}
            className="mj-dsheet-loadmore"
          >
            {t("discover.load_more")}
          </button>
        </div>
      )}
    </>
  );
}

function MJCard({
  item,
  isLiked,
  onToggleLike,
  onSelect,
}: {
  readonly item: DiscoverItem;
  readonly isLiked: boolean;
  readonly onToggleLike: () => void;
  readonly onSelect?: () => void;
}) {
  const { t } = useTranslation();
  const itemPrice = useItemPrice();
  const isBookable = item.mode === "bookable";
  const priceIcon = getPriceIcon(priceKind(item));
  const priceLabelKey = getPriceLabelKey(priceKind(item));
  const priceLabel = priceLabelKey ? t(priceLabelKey) : undefined;

  return (
    <article
      className="group relative flex w-full flex-col overflow-hidden rounded-[22px] bg-white p-2 shadow-[0_4px_20px_rgba(4,0,51,0.08),0_1px_4px_rgba(4,0,51,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-[3px] hover:shadow-[0_18px_44px_rgba(4,0,51,0.12)]"
    >
      <button
        type="button"
        aria-label={t("discover.open_item", { title: item.title })}
        onClick={onSelect}
        className="block text-start"
      >
        <div className="relative aspect-square w-full overflow-hidden rounded-[16px]">
          {item.image ? (
            <img
              src={thumbUrl(item.image, 480)}
              srcSet={thumbSrcSet(item.image, [240, 480, 720])}
              sizes="50vw"
              alt={item.imageAlt ?? item.title}
              loading="lazy"
              className="h-full w-full rounded-[16px] object-cover object-center"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[#040033]/[0.06] text-[#040033]/30">
              <Icon name="ri-image-line" className="text-[32px]" aria-hidden="true" />
            </div>
          )}
          {item.subtitle && (
            <span className="absolute start-2.5 top-2.5 inline-flex min-h-[26px] items-center whitespace-nowrap rounded-full bg-[#040033] px-3 font-sans text-[10px] font-bold uppercase leading-[14px] tracking-[0.08em] text-white">
              {item.subtitle}
            </span>
          )}
        </div>
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleLike();
        }}
        aria-pressed={isLiked}
        aria-label={isLiked ? t("place.unsave") : t("activity.save")}
        className="absolute end-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[#040033] shadow-[0_2px_8px_rgba(4,0,51,0.18)] backdrop-blur transition hover:bg-white"
      >
        <Icon name={isLiked ? "ri-heart-3-fill text-[#e11d48]" : "ri-heart-3-line"} aria-hidden="true" />
      </button>

      <div className="flex flex-1 flex-col gap-[5px] px-1 pb-1 pt-2.5">
        <h3 className="m-0 line-clamp-2 flex-1 font-sans text-[13px] font-bold leading-[1.3] text-[#040033]">
          {item.title}
        </h3>
        {/* Wraps rather than truncates. Two across on a 375px phone leaves this
            row about 150px wide, and a one-line cut turned CASABLANCA into
            "CASABLA…", which reads as a rendering fault rather than as a place.
            The same fix the grid card carries. */}
        <div className="flex flex-row flex-wrap items-center justify-between gap-1.5">
          <span className="inline-flex items-center rounded-full bg-[#040033]/[0.06] px-2.5 py-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#040033]">
            {item.location}
          </span>
          {isBookable && itemPrice(item) && (
            <div className="flex shrink-0 items-center gap-[5px] whitespace-nowrap text-[11px] font-extrabold leading-[1.3] text-[#040033]">
              <span>{itemPrice(item)}</span>
              <span className="h-3 w-px bg-[#040033]/20" />
              <Icon name={priceIcon} className={`text-[12px] text-[#040033]/60`} aria-hidden="true"
                title={priceLabel ?? t("activity.person")} />
            </div>
          )}
        </div>
        <CardCTA item={item} isBookable={isBookable} />
      </div>
    </article>
  );
}

function CardCTA({
  item,
  isBookable,
}: {
  readonly item: DiscoverItem;
  readonly isBookable: boolean;
}) {
  const { t } = useTranslation();
  const params = useParams({ strict: false }) as { lang?: string };
  const lang = params.lang ?? "en";
  const className =
    "mt-1.5 flex min-h-[38px] w-full items-center justify-center gap-1.5 rounded-full px-3.5 font-sans text-[12px] font-bold tracking-[0.02em] no-underline transition-opacity duration-200 hover:opacity-90 " +
    (isBookable ? "bg-[#040033] text-white" : "bg-[#040033]/[0.06] text-[#040033]");
  const label = isBookable ? t("activity.book_now") : t("activity.explore");
  if (!item.sourceSlug) {
    return (
      <button type="button" className={className}>
        {label}
      </button>
    );
  }
  const city = item.citySlug?.trim() || "morocco";
  const category =
    item.categorySlug?.trim() || item.segment?.trim() || "place";
  return (
    <Link
      to={
        isBookable
          ? "/$lang/$city/$category/$slug/packages"
          : "/$lang/$city/$category/$slug"
      }
      params={{ lang, city, category, slug: item.sourceSlug }}
      className={className}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
}

// An empty grid needs a way out of it. Two lines of advice pointed at a reset
// control the traveller has already scrolled past — and could not see at all
// once the sheet was expanded — so the way out is here, next to the problem,
// and clears search, category, sub-tags, city and the map's narrowing in one
// press.
function EmptyState({
  inView = false,
  failed = false,
  onClear,
}: {
  readonly inView?: boolean;
  /** The catalogue did not load: say so, rather than "No picks here yet". */
  readonly failed?: boolean;
  readonly onClear?: () => void;
}) {
  const { t } = useTranslation();
  if (failed) {
    return (
      <div className="mx-3 rounded-[20px] bg-white px-6 py-10 text-center shadow-[0_4px_16px_rgba(4,0,51,0.05)] sm:mx-5">
        <Icon name="ri-refresh-line" className="text-[28px] text-[#040033]/40" aria-hidden="true" />
        <p className="mt-2 font-sans text-[14px] font-bold text-[#040033]">{t("discover.load_failed")}</p>
      </div>
    );
  }
  return (
    <div className="mx-3 rounded-[20px] bg-white px-6 py-10 text-center shadow-[0_4px_16px_rgba(4,0,51,0.05)] sm:mx-5">
      <Icon name="ri-search-eye-line" className="text-[28px] text-[#040033]/40" aria-hidden="true" />
      <p className="mt-2 font-sans text-[14px] font-bold text-[#040033]">{t("discover.empty_no_picks")}</p>
      <p className="mt-1 font-sans text-[12px] text-[#5a5c75]">
        {t(inView ? "discover.empty_in_view" : "discover.empty_try_other")}
      </p>
      {onClear && (
        <div className="mt-4">
          <button
            type="button"
            onClick={onClear}
            className="mt-1.5 flex min-h-[38px] w-full items-center justify-center gap-1.5 rounded-full bg-[#040033] px-3.5 font-sans text-[12px] font-bold tracking-[0.02em] text-white no-underline transition-opacity duration-200 hover:opacity-90"
          >
            {t("discover.empty_clear_filters", { defaultValue: "Clear filters" })}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * What the price is per. A hotel's pin label is localized ("Hôtel", "فندق"),
 * so the segment decides for stays rather than the English word match below.
 */
function priceKind(item: DiscoverItem): string | undefined {
  return item.segment === "stays" ? "stay" : (item.pinLabel ?? item.subtitle);
}

function getPriceIcon(category?: string): string {
  const c = (category ?? "").toLowerCase();
  if (/(stay|hotel|riad|room|lodge|camp|villa)/.test(c)) return "ri-hotel-bed-line";
  if (/(trip|excursion|workshop|tour|group|boat|pool|jet ski)/.test(c)) return "ri-group-line";
  return "ri-user-line";
}

function getPriceLabelKey(category?: string): string | undefined {
  const c = (category ?? "").toLowerCase();
  if (/(stay|hotel|riad|room|lodge|camp|villa)/.test(c)) return "activity.night";
  if (/(trip|excursion|workshop|tour|group|boat|pool|jet ski)/.test(c)) return "activity.per_group";
  return undefined;
}
